import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { MenuItem, DropdownButton } from 'react-bootstrap';
import { carrierEdiEvents } from 'components/modules/shipments/mappings';
import _ from 'underscore';
import { humanize } from 'underscore.string';
import SessionStore from 'stores/SessionStore';

import Checkbox from '../../../v2/forms/Checkbox';
import CargomaticCard from '../../../v2/card';
import RenderSeriesDropdown from '../rate-series-dropdown';

const ENABLED_EVENTS = [
  'load_tendered',
  'load_canceled',
  'load_updated',
  'payout_paid',
  'carrier_accessorial_pending_admin',
  'carrier_accessorial_pending_shipper',
  'carrier_accessorial_accepted',
  'carrier_accessorial_approved',
  'carrier_accessorial_declined',
];

const statuses = carrierEdiEvents.map((status) => status.slug);
const items = carrierEdiEvents.map((event) => (
  <option key={event.status} value={event.status}>
    {event.message}
  </option>
));

class CarrierEDISettings extends React.Component {
  constructor(props) {
    super(props);

    this.state = this.getInitState(props);
  }

  getMenuItems = () => {
    const { t } = this.props;
    return carrierEdiEvents.map(({ slug: status, localizationSlug }) => (
      <option key={status} value={status}>
        {humanize(t(localizationSlug))}
      </option>
    ));
  };

  getAvailableStatus = () => {
    return carrierEdiEvents.map(({ slug }) => slug);
  };

  getInitState = (props) => {
    const { carrier } = props;

    const customerEvents =
      carrier.customer_configurations &&
      carrier.customer_configurations.enabled_events
        ? carrier.customer_configurations.enabled_events
        : {};

    return {
      scac_code: carrier.scac_code,
      edi_version: carrier.edi_version,
      events: customerEvents,
      disabled: false,
      availableStatuses: statuses,
      menuItems: items,
      canEdit: SessionStore.getUserRoles().some((role) =>
        ['admin', 'integration_manager'].includes(role),
      ),
      selectedSeriesId: carrier.series_id || null,
    };
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props, nextProps)) {
      this.setState(this.getInitState(nextProps));
    }
  }

  isValid = () => {
    if (this.state.edi_version === 2 && !this.state.scac_code) return false;

    return true;
  };

  handleScacCodeChange = (scac_code) => {
    this.setState({ scac_code });
  };

  handleEdiVersionSelect = (edi_version) => {
    this.setState({ edi_version });
  };

  handleAddEvent = (status) => (checked) => {
    this.setState((prevState) => {
      const events = {
        ...prevState.events,
        [status]: checked,
      };

      return {
        events,
      };
    });
  };

  handleSeriesSelect = (selectedSeriesId) => {
    this.setState({ selectedSeriesId });
  };

  renderEvents = () => {
    const { t } = this.props;
    const { events } = this.state;
    return carrierEdiEvents.map((event) => {
      const { slug: status } = event;
      const checked = events[status];
      let disabled = !ENABLED_EVENTS.includes(status);

      if (status === 'accepted' && !disabled) {
        const orderAcceptedChecked = events.order_accepted;
        if (orderAcceptedChecked) {
          disabled = true;
        }
      } else if (status === 'order_accepted' && !disabled) {
        const acceptedChecked = events.accepted;
        if (acceptedChecked) {
          disabled = true;
        }
      }

      return [
        <Checkbox
          disabled={disabled}
          checked={checked}
          onChange={this.handleAddEvent(status)}
        >
          {t(event.localizationSlug)}
        </Checkbox>,
      ];
    });
  };

  saveCustomer = () => {
    const update = {
      edi_version: this.state.edi_version,
      scac_code: this.state.scac_code,
      customer_configurations: {
        enabled_events: this.state.events,
        seriesId: this.state.selectedSeriesId || null,
      },
    };
    const carrier = _.extend({ _id: this.props.carrier._id }, update);
    this.props.onCustomerPreferencesChange('Carrier', carrier);
  };

  render() {
    const events = this.renderEvents();
    const { t } = this.props;
    return (
      <CargomaticCard
        header={() => <h3 className="my-0">{t('INTEGRATION_SETTINGS')}</h3>}
        footer={() => (
          <div className="form-actions">
            <button
              className="button button-green"
              disabled={!this.state.canEdit || !this.isValid()}
              onClick={this.saveCustomer}
            >
              {t('SAVE')}
            </button>
          </div>
        )}
        grayFooter
      >
        <div className="lg:grid lg:grid-cols-2 lg:gap-4">
          <div className="mb-4">
            <label className="block mb-2 form-label">
              {t('CUSTOMER_SCAC_CODE')}
            </label>
            <input
              className="w-full mb-4 form-field"
              type="text"
              placeholder={t('CUSTOMER_SCAC_CODE')}
              value={this.state.scac_code}
              onChange={(evt) => this.handleScacCodeChange(evt.target.value)}
            />
            <p className="text-sm">{t('UNIQUE_SCAC_MESSAGE')}</p>
          </div>
          <div className="mb-4">
            <label className="block mb-2 form-label">{t('EDI_VERSION')}</label>
            <DropdownButton
              title={
                this.state.edi_version
                  ? t('VERSION', { version: this.state.edi_version })
                  : t('SELECT_EDI_VERSION')
              }
              onSelect={(key) => this.handleEdiVersionSelect(key)}
            >
              <MenuItem eventKey="1">{t('EDI_VERSION_1')}</MenuItem>
              <MenuItem eventKey="2">{t('EDI_VERSION_2')}</MenuItem>
              <MenuItem eventKey="3">{t('EDI_VERSION_3')}</MenuItem>
            </DropdownButton>
            <p className="text-sm">{t('EDI_VERSION_2_MSG')}</p>
          </div>
        </div>
        <div className="mb-4">
          <div>
            <label className="block form-label">{t('ENABLE_EVENTS')}</label>
            <p className="text-sm">{t('SELECT_EVENTS_FOR_CUSTOMER')}</p>
          </div>
          {events}
        </div>
        <RenderSeriesDropdown
          selectedSeriesId={this.state.selectedSeriesId}
          customerType="carrier"
          handleSeriesSelect={this.handleSeriesSelect}
        />
      </CargomaticCard>
    );
  }
}

CarrierEDISettings.propTypes = {
  carrier: PropTypes.object.isRequired,
};
export default withTranslation()(CarrierEDISettings);
